import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Arcane JavaScript knowledge still useful",
  "description": "",
  "published": "2017-11-17T00:00:00.000Z",
  "redirect_from": ["/blog/arcane-javascript-knowledge-still-useful/swizec/7932"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`ES6 has been with us for 2 years. `}<a parentName="p" {...{
        "href": "https://es2017.io"
      }}>{`ES2016 and ES2017`}</a>{` are standard practice. ES2018 is just around the corner.`}</p>
    <p>{`And yet, sometimes you still need JavaScript practices so arcane you've almost forgotten they exist. Such was the case with a production bug we discovered after a performance optimization.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`When you get `}<br parentName="p"></br><br parentName="p"></br>{`Uncaught TypeError: Cannot read property 'call' of undefined`}<br parentName="p"></br>{`    at __webpack_require__`}<br parentName="p"></br><br parentName="p"></br>{`But only on every 4th page load. Sometimes 3rd. Or 7th. `}<a parentName="p" {...{
          "href": "https://t.co/7T7gXFwQmi"
        }}>{`pic.twitter.com/7T7gXFwQmi`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/928761345750605825"
      }}>{`November 9, 2017`}</a></blockquote>
    <p>{`Every once in a while, you would refresh `}<a parentName="p" {...{
        "href": "https://www.yup.com"
      }}>{`our webapp`}</a>{` and stare at the loading animation forever. Yes, we have one of those because we're cool.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/yuploader.gif",
        "alt": null
      }}></img></p>
    <p>{`We worked hard on it, and it looks great. But we don't want you to be stuck staring at it never getting to the page.`}</p>
    <p>{`We traced the problem down to a JavaScript error during app initialization. Sometimes Webpack would try to execute modules before they were ready.`}</p>
    <p>{`This shows up as a cryptic error 👇`}</p>
    <pre><code parentName="pre" {...{}}>{`Uncaught TypeError: Cannot read property 'call' of undefined
    at __webpack_require__
`}</code></pre>
    <p>{`It happens on this line inside `}<inlineCode parentName="p">{`manifest.js`}</inlineCode>{` 👇`}</p>
    <pre><code parentName="pre" {...{}}>{`// Execute the module function
modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
`}</code></pre>
    <p>{`Now `}<inlineCode parentName="p">{`manifest.js`}</inlineCode>{` is a generated file, so there isn't much you can do to fix it. In fact, you shouldn't have to fix it. Or even consider modifying it. Ever.`}</p>
    <p>{`Webpack generates this file when you compile your code and uses it to bootstrap your code. It builds a list of modules inside that `}<inlineCode parentName="p">{`modules`}</inlineCode>{` dictionary and executes them when you run `}<inlineCode parentName="p">{`import`}</inlineCode>{` or `}<inlineCode parentName="p">{`require()`}</inlineCode>{`.`}</p>
    <p>{`Usually, this either always works or never works.`}</p>
    <p>{`It always works when your code is correct. It never works when you forget to `}<inlineCode parentName="p">{`export`}</inlineCode>{` your module.`}</p>
    <p>{`But it's never supposed to get into such a situation that it `}<em parentName="p">{`sometimes`}</em>{` works and sometimes doesn't. That is right out.`}</p>
    <p>{`So I started to dig.`}</p>
    <p>{`And dig.`}</p>
    <p>{`And dig some more.`}</p>
    <p>{`Our bug stymied even grandmaster TheLarkInn.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "und",
        "dir": "ltr"
      }}>{`Wat.`}</p>{`— 💎🙌🚀🚀 Sean Larkin (@TheLarkInn) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/TheLarkInn/status/928789785124016128"
      }}>{`November 10, 2017`}</a></blockquote>
    <p>{`I dug for an hour. Then two. Then five.`}</p>
    <p>{`I tried everything. I added console logs into manifest.js to track which module exactly was causing a problem. I used `}<a parentName="p" {...{
        "href": "https://webpack.github.io/analyse/"
      }}>{`Webpack's official analyze tool`}</a>{` to inspect our builds and associate `}<inlineCode parentName="p">{`moduleId`}</inlineCode>{`s with specific code files.`}</p>
    <p>{`At up to 3 minutes per compile, it was slow going.`}</p>
    <p>{`I changed this and that and nothing worked.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/tired-at-computer.gif",
        "alt": null
      }}></img></p>
    <p>{`Then I found this old article Jake Archibald published in 2013. `}<a parentName="p" {...{
        "href": "https://www.html5rocks.com/en/tutorials/speed/script-loading/"
      }}>{`Deep dive into the murky waters of script loading`}</a></p>
    <p>{`In it, he explained that scripts dynamically inserted into the DOM were `}<inlineCode parentName="p">{`async`}</inlineCode>{` by default.`}</p>
    <p>{`💡`}</p>
    <p>{`Why does that matter? Because our performance optimization made our scripts `}<inlineCode parentName="p">{`preload`}</inlineCode>{` then injected them into the DOM when the files were loaded.`}</p>
    <p>{`This meant that `}<em parentName="p">{`sometimes`}</em>{` our `}<inlineCode parentName="p">{`main_code.js`}</inlineCode>{` would start executing `}<em parentName="p">{`before`}</em>{` our `}<inlineCode parentName="p">{`main_code_vendor.js`}</inlineCode>{` file was loaded. As a result, core libraries our code depends on weren't loaded yet by the time our code tried to use them.`}</p>
    <p>{`Ha!`}</p>
    <h2 {...{
      "id": "some-background"
    }}>{`Some background`}</h2>
    <p>{`Let me give you some background.`}</p>
    <p>{`We use `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/dirty-hack-took-30percent-off-webpack-size/swizec/7657"
      }}>{`Webpack to split our app into multiple files`}</a>{`. Libraries go into an `}<inlineCode parentName="p">{`app_vendor.js`}</inlineCode>{` file, and our code goes into an `}<inlineCode parentName="p">{`app.js`}</inlineCode>{` file. Most of our apps also have chunks that Webpack loads asynchronously when they're needed.`}</p>
    <p>{`So you need to load at least 3 JavaScript files to make any of our apps work`}</p>
    <ol>
      <li parentName="ol">{`manifest.js`}</li>
      <li parentName="ol">{`app_vendor.js`}</li>
      <li parentName="ol">{`app.js`}</li>
    </ol>
    <p>{`Loading scripts as `}<inlineCode parentName="p">{`async`}</inlineCode>{` is an old technique to make webapps faster. You write `}<inlineCode parentName="p">{`<script src="bla" async></script>`}</inlineCode>{` and the browser doesn't wait for JavaScript to load before moving on to rendering the rest of your DOM.`}</p>
    <p>{`This is great, but it leads to problems. Scripts might execute in random order.`}</p>
    <p>{`So instead, we used `}<inlineCode parentName="p">{`defer`}</inlineCode>{` for a long time. This downloads scripts without waiting for them, then executes all of them in order as they were defined.`}</p>
    <p>{`Wonderful.`}</p>
    <p>{`But preloading is even better 👉 `}<inlineCode parentName="p">{`<link rel="preload" href="bla" onload="loadJsFiles(this.href) />`}</inlineCode>{`. With preloading, you're loading scripts without waiting for them, potentially before the user even opens your site, then executing a callback to say `}<em parentName="p">{`"Ok we got the script, now what?"`}</em></p>
    <p>{`In our case, the `}<em parentName="p">{`"now what"`}</em>{` part would create a `}<inlineCode parentName="p">{`script`}</inlineCode>{` DOM node and insert it into the page. That makes it execute.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function loadJsFile(file) {
  // keep track of what's been loaded
  if (allScriptsLoaded) {
    loadedScripts.forEach(function () {
      var script = document.createElement("script");
      script.src = '<%= "#{js_file_href}" %>';
      document.body.appendChild(script);
    });
  }
}
`}</code></pre>
    <p>{`Keep track of all scripts that were loaded, insert them into the DOM, and trigger their execution when all are ready.`}</p>
    <h2 {...{
      "id": "the-solution"
    }}>{`The solution`}</h2>
    <p>{`This `}<em parentName="p">{`"Preload as much as possible, insert into DOM when all is ready"`}</em>{` works great.`}</p>
    <p>{`Except when it doesn't.`}</p>
    <p><em parentName="p">{`Sometimes`}</em>{` they would execute in the wrong order. Our business code would start executing before our vendor code and discover that the libraries it needs weren't there.`}</p>
    <p>{`💩`}</p>
    <p>{`But why? We wait until all scripts are preloaded before inserting them into the page.`}</p>
    <p>{`Because dynamically inserted scripts are `}<inlineCode parentName="p">{`async`}</inlineCode>{` by default. You have explicitly disable that with `}<inlineCode parentName="p">{`script.async = false`}</inlineCode>{`.`}</p>
    <p>{`And everything works.`}</p>
    <p>{`2013-era JavaScript strikes again. Still relevant.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/cosby-facepalm.gif",
        "alt": null
      }}></img></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      